import { messages } from '@databases/intl/messages';
import { defaultStoreUtils } from '@databases/redux';
import SelectOptionTag from '@databases/tag/SelectOptionTag';
import { DatabaseFieldType } from '@databases/types/database';
import { MultiValueChangeType } from '@databases/utils/database/changesets';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import React, { type FC, useCallback, useRef } from 'react';
import { useIntl } from 'react-intl-next';
import styled from 'styled-components';

import { PopoverEdit } from '../PopoverEdit';
import { EditComponentProps } from '../types';

import TagSelect from './TagSelect';

const TagValueContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const TagPopoverEdit: FC<EditComponentProps<DatabaseFieldType.SELECT>> = ({
  onChange,
  onFocus,
  onBlur,
  autoFocus,
  value,
  field,
  addChangeToDraft,
  contentId,
  placeholder,
  appearance,
  isDefaultValue,
  isFilterValueEdit,
}) => {
  const hasCurrentData = value.length > 0;
  const { formatMessage } = useIntl();
  const addDataSectionRef = useRef<HTMLDivElement | null>(null);
  const currentDataSectionRef = useRef<HTMLDivElement | null>(null);
  const canCreate = defaultStoreUtils.useSelector((state) => {
    const database =
      contentId != null
        ? selectDatabaseByContentIdRequired(state, { contentId })
        : undefined;
    return database && database.operations.updateMetadata && !database.meta.isLocked;
  });
  const isMulti = field.settings.multi || value.length > 1;
  const isRenderedInSidePanel = isDefaultValue || isFilterValueEdit;

  const handleRemove = useCallback(
    async (removedValues: string[], allSelectedValues: string[]) => {
      await onChange(allSelectedValues, {
        type: DatabaseFieldType.SELECT,
        delta: {
          type: MultiValueChangeType.REMOVE,
          index: value.indexOf(removedValues[0]), // FIXME: index?
        },
      });

      // If we are removing the last value, focus the select input
      if (value.length === 1) {
        addDataSectionRef.current?.getElementsByTagName('input')[0].focus();
      }
    },
    [onChange, value]
  );

  const extractOptionDataFromFields = useCallback(
    (option: string) => {
      const fieldData = field.settings.selectOptions.find((o) => o.id === option);
      return {
        label:
          fieldData?.label ?? formatMessage(messages['board-layout.empty-tag-label']),
        id: fieldData?.id,
        color: fieldData?.color ?? 'greyLight',
      };
    },
    [field, formatMessage]
  );

  const TagSelectAddDataSection = (
    <div ref={addDataSectionRef}>
      <TagSelect
        options={field.settings.selectOptions}
        value={value}
        isCreatable={canCreate}
        isMulti={isMulti}
        onFocus={onFocus}
        onBlur={onBlur}
        autoFocus={autoFocus}
        placeholder={placeholder}
        preventOpenWhenNoOptions={false}
        appearance={appearance}
        addChangeToDraft={addChangeToDraft}
        onChange={onChange}
        field={field}
        isRenderedInSidePanel={isRenderedInSidePanel}
      />
    </div>
  );

  const TagSelectCurrentDataSection = (
    <TagValueContainer data-popover-edit-focus-parent ref={currentDataSectionRef}>
      {value.map((option) => (
        <SelectOptionTag
          key={option}
          option={extractOptionDataFromFields(option)}
          isMulti={isMulti}
          isRemovable
          onRemove={() => handleRemove([option], value)}
        />
      ))}
    </TagValueContainer>
  );

  return (
    <PopoverEdit
      isMultiSelect={field.settings.multi}
      hasCurrentData={hasCurrentData}
      currentDataSection={TagSelectCurrentDataSection}
      addDataSection={TagSelectAddDataSection}
      isRenderedInSidePanel={isRenderedInSidePanel}
      fieldWidth={field.width}
    />
  );
};

export default TagPopoverEdit;
